import { Briefcase } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useWatch } from "react-hook-form";

import {
  AddressFields,
  CheckboxField,
  DateField,
  FormRow,
  FormSheet,
  FormSubheading,
  PhoneField,
  TextareaField,
  TextField
} from "@/components/form";
import { Separator } from "@/components/ui/separator";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { ForeignNationalEmploymentHistoryEntry } from "../../../../api/types/foreignNationalEmploymentHistory";
import { CoverageBar, GapRow } from "../../components/CoverageBar";
import {
  AgentReadCard,
  FillFromDocuments
} from "../../components/FillFromDocuments";
import { SourceCell } from "../../components/SourceTag";
import {
  ClientTable,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import { ClientTableToolbar } from "../../components/table/ClientTableToolbar";
import {
  CheckCell,
  DateRangeCell,
  MutedCell,
  SkeletonRow,
  TitleCell
} from "../../components/table/cells";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { useHistoryCoverage } from "../../hooks/useHistoryCoverage";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import { addressFromRaw } from "../../lib/address";
import { normalizeText } from "../../lib/format";
import { dateRangeLabel } from "../../lib/records";
import { NAV_ITEMS } from "../../navConfig";
import {
  employerLabel,
  employmentDraftSchema,
  employmentDraftToPayload,
  employmentEntryToDraft,
  employmentFocusDomId,
  emptyEmploymentDraft,
  sortEmploymentEntries,
  type EmploymentDraft,
  type EmploymentPayload
} from "./employmentModel";

const getId = (e: ForeignNationalEmploymentHistoryEntry) =>
  e.employment_history_id;

const COLUMNS: ClientTableColumn[] = [
  { id: "title", header: "Job Title" },
  { id: "employer", header: "Employer" },
  { id: "location", header: "Location" },
  { id: "dates", header: "Dates" },
  { id: "current", header: "Current", className: "w-30" },
  { id: "source", header: "Source", className: "w-30" }
];

const periodOf = (row: {
  entry: ForeignNationalEmploymentHistoryEntry;
  title: string;
  employer: string;
}) => ({
  start: row.entry.start_date,
  end: row.entry.end_date,
  current: Boolean(row.entry.is_current),
  label: `${row.title} ${row.employer} ${row.entry.notes ?? ""}`
});

const employerLocation = (e: ForeignNationalEmploymentHistoryEntry): string => {
  const a = addressFromRaw(e.employer?.businessAddress);
  return (
    [[a.city, a.state].filter(Boolean).join(", "), a.country]
      .filter(Boolean)
      .join(" · ") || "—"
  );
};

/** Employment history: employers with contact details and role, in a Sheet editor. */
export const EmploymentTab: FC = () => {
  const { clientId, url } = useClientProfile();
  const [search, setSearch] = useState("");

  useEffect(() => setSearch(""), [clientId]);

  const crud = useRecordCrud<
    ForeignNationalEmploymentHistoryEntry,
    EmploymentDraft,
    EmploymentPayload
  >({
    clientId,
    noun: "Employment",
    list: ForeignNationalsAPI.listEmploymentHistoryEntries,
    create: ForeignNationalsAPI.createEmploymentHistoryEntry,
    update: ForeignNationalsAPI.updateEmploymentHistoryEntry,
    remove: ForeignNationalsAPI.deleteEmploymentHistoryEntry,
    getId,
    toDraft: employmentEntryToDraft,
    emptyDraft: emptyEmploymentDraft,
    toPayload: employmentDraftToPayload,
    schema: employmentDraftSchema,
    sort: sortEmploymentEntries
  });

  const { highlightedId } = useFocusScroll(
    url.state.focus.employmentHistoryId
      ? employmentFocusDomId(url.state.focus.employmentHistoryId)
      : null,
    !crud.isLoading && crud.records.length > 0
  );

  const rows = useMemo(() => {
    const all = crud.records.map((e) => {
      const title = normalizeText(e.job_title) || "—";
      const employer = employerLabel(e);
      const location = employerLocation(e);
      const dates = dateRangeLabel(e.start_date, e.end_date, e.is_current);
      return {
        entry: e,
        id: getId(e),
        domId: employmentFocusDomId(getId(e)),
        title,
        employer,
        location,
        isCurrent: Boolean(e.is_current),
        haystack: `${title} ${employer} ${location} ${dates} ${
          e.job_description ?? ""
        } ${e.notes ?? ""}`.toLowerCase()
      };
    });
    const q = search.trim().toLowerCase();
    return q ? all.filter((r) => r.haystack.includes(q)) : all;
  }, [crud.records, search]);

  const searching = search.trim().length > 0;
  const { coverage, items } = useHistoryCoverage(
    "employment",
    rows,
    periodOf,
    !crud.isLoading && !searching && crud.records.length > 0
  );

  const { form } = crud;
  const isEditing = crud.editor.mode === "edit";
  const isCurrent = useWatch({ control: form.control, name: "isCurrent" });

  // A current job has no end date.
  useEffect(() => {
    if (isCurrent && form.getValues("endDate")) {
      form.setValue("endDate", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [form, isCurrent]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.employment.label}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search employment...",
          ariaLabel: "Search employment"
        }}
        onRefresh={() => crud.refresh().catch(() => undefined)}
        isRefreshing={crud.isLoading}
        secondaryActions={<FillFromDocuments />}
        primaryAction={{
          label: "Add Employment",
          onClick: () => crud.openCreate()
        }}
      />
      <CoverageBar kind="employment" coverage={coverage} />
      <ClientTable
        ariaLabel="Employment history"
        columns={COLUMNS}
        isLoading={crud.isLoading}
        isEmpty={rows.length === 0}
        error={crud.error}
        onRetry={() => crud.refresh().catch(() => undefined)}
        renderSkeletonRow={(i) => (
          <SkeletonRow
            key={i}
            widths={["w-40", "w-40", "w-28", "w-32", "w-12", "w-16"]}
          />
        )}
        empty={
          crud.records.length === 0
            ? {
                title: "No Employment Yet",
                icon: Briefcase,
                message:
                  "Track your client's work history so petitions and forms stay accurate.",
                extra: (
                  <AgentReadCard
                    finds="Each job it finds"
                    addLabel="Add Employment"
                    onAdd={() => crud.openCreate()}
                  />
                )
              }
            : {
                title: "No Matching Positions",
                icon: Briefcase,
                message: "Try a different search."
              }
        }
      >
        {(searching || !coverage
          ? rows.map((row) => ({ type: "row" as const, row, faded: false }))
          : items
        ).map((item) =>
          item.type === "gap" ? (
            <GapRow
              key={`gap-${item.gap.start.valueOf()}`}
              gap={item.gap}
              columns={COLUMNS.length}
              datesColumn={3}
              kind="employment"
            />
          ) : (
            <ClientTableRow
              key={item.row.id}
              domId={item.row.domId}
              highlighted={highlightedId === item.row.domId}
              onActivate={() => crud.openEdit(item.row.entry)}
              className={item.faded ? "opacity-50" : undefined}
            >
              <TitleCell
                icon={Briefcase}
                iconClassName={
                  item.row.isCurrent ? "bg-brand-50 text-brand-600" : undefined
                }
                title={item.row.title}
              />
              <MutedCell>{item.row.employer}</MutedCell>
              <MutedCell>{item.row.location}</MutedCell>
              <DateRangeCell
                start={item.row.entry.start_date}
                end={item.row.entry.end_date}
                isCurrent={item.row.isCurrent}
              />
              <CheckCell checked={item.row.isCurrent} />
              <SourceCell source={item.row.entry.source} />
            </ClientTableRow>
          )
        )}
      </ClientTable>

      <FormSheet
        form={form}
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        title={isEditing ? "Edit employment" : "Add employment"}
        description="The employer, a contact there, and the client's role."
        onSubmit={crud.submit}
        isEditing={isEditing}
        isSubmitting={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this employment entry?"
        size="lg"
      >
        <FormSubheading>Employer</FormSubheading>
        <FormRow>
          <TextField<EmploymentDraft>
            name="employer.legalBusinessName"
            label="Business Name"
            required
            placeholder="Enter employer name"
            autoComplete="organization"
          />
          <TextField<EmploymentDraft>
            name="employer.dba"
            label="DBA"
            placeholder="Doing business as"
          />
        </FormRow>
        <AddressFields<EmploymentDraft>
          name="employer.businessAddress"
          required
        />

        <Separator />
        <FormSubheading>Employer contact</FormSubheading>
        <FormRow>
          <TextField<EmploymentDraft>
            name="employer.contactName"
            label="Contact Name"
            required
            placeholder="Enter contact name"
          />
          <TextField<EmploymentDraft>
            name="employer.contactTitle"
            label="Contact Title"
            required
            placeholder="Enter contact title"
          />
        </FormRow>
        <FormRow>
          <TextField<EmploymentDraft>
            name="employer.contactEmail"
            label="Contact Email"
            type="email"
            required
            placeholder="Enter contact email"
            autoComplete="email"
          />
          <PhoneField<EmploymentDraft>
            name="employer.contactPhone"
            label="Contact Phone"
            required
            placeholder="Enter contact phone"
          />
        </FormRow>

        <Separator />
        <FormSubheading>Role</FormSubheading>
        <TextField<EmploymentDraft>
          name="jobTitle"
          label="Job Title"
          required
          placeholder="Enter job title"
        />
        <TextareaField<EmploymentDraft>
          name="jobDescription"
          label="Job Description"
          required
          rows={4}
          placeholder="Describe the role and responsibilities..."
        />
        <FormRow>
          <DateField<EmploymentDraft> name="startDate" label="From" required />
          <DateField<EmploymentDraft>
            name="endDate"
            label="To"
            disabled={isCurrent}
            placeholder={isCurrent ? "Present" : "Pick a date"}
          />
        </FormRow>
        <CheckboxField<EmploymentDraft>
          name="isCurrent"
          label="This is the client's current employer"
        />
        <TextareaField<EmploymentDraft>
          name="notes"
          label="Notes"
          placeholder="Add any additional notes about this employment..."
        />
      </FormSheet>
    </div>
  );
};
